import type { KeyboardEvent, MouseEvent } from "react";
import { useEffect, useState } from "react";
import { isWindows } from "react-device-detect";
import { styled } from "styled-components";

import { callToActionApiSlice } from "api/callToActionApi.slice";
import { CheckIcon, Spinner, StyledModalText } from "common/components";
import { LanguageCode } from "common/types/languageCode";
import { useDispatch, useSelector } from "common/utilities/react-redux";
import { useTranslate } from "common/utilities/useTranslate";
import { fetchTerms } from "domains/language/actions";
import { getIsFetchingTerms, getSiteLanguage } from "domains/language/selectors";
import { ModalHeader } from "domains/modal/components/ModalHeader";
import { getLanguages } from "entities/languages/selectors";

const StyledRow = styled.div<{ $isSelected: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 0;
  cursor: pointer;
  opacity: ${({ $isSelected }) => ($isSelected ? 1 : 0.7)};
  transition: opacity 0.3s ease-in-out;

  &:focus-visible,
  &:hover {
    opacity: 1;
  }
`;

const StyledSpinner = styled(Spinner)`
  width: 16px;
  height: 16px;
  color: ${({ theme }) => theme.colors.textBright};
`;

export const ChangeLanguageModal = () => {
  const dispatch = useDispatch();
  const isFetchingTerms = useSelector(getIsFetchingTerms);
  const siteLanguage = useSelector(getSiteLanguage);
  const languages = useSelector(getLanguages);
  const translate = useTranslate();

  const [nextLanguageId, setNextLanguageId] = useState<string | null>(null);

  const handleClick = (event: MouseEvent, id: LanguageCode) => {
    event.preventDefault();
    selectLanguageById(id);
  };

  const handleKeyDown = (event: KeyboardEvent, id: LanguageCode) => {
    if (event.key !== "Enter" && event.key !== " ") {
      return;
    }

    event.preventDefault();
    selectLanguageById(id);
  };

  const selectLanguageById = (languageId: LanguageCode) => {
    if (isFetchingTerms || siteLanguage === languageId) {
      return;
    }
    setNextLanguageId(languageId);
    dispatch(fetchTerms({ language: languageId }));
    dispatch(callToActionApiSlice.util.invalidateTags([{ type: "CallToAction" }]));
  };

  // On Windows, the Chinese language option via the code `zh` doesn't seem to be detected correctly (says unknown language when passing it to `lang` prop) compared to Mac, so this is used to force the correct narration to occur.
  const chineseAriaText = translate("app.languages.chinese");

  const getLanguageCodeAriaLabel = (id: LanguageCode, label: string) => {
    if (!isWindows) return;

    if (id === LanguageCode.ZH) return chineseAriaText;

    return label;
  };

  useEffect(() => {
    if (siteLanguage === nextLanguageId && !isFetchingTerms) {
      setNextLanguageId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteLanguage, isFetchingTerms]);

  return (
    <>
      <ModalHeader title={translate("app.language_chooser.title")} />
      <StyledModalText>{translate("app.language_chooser.text")}</StyledModalText>
      {languages.map(({ id, label }) => (
        <StyledRow
          key={id}
          onClick={(event) => handleClick(event, id)}
          onKeyDown={(event) => handleKeyDown(event, id)}
          $isSelected={siteLanguage === id}
          tabIndex={0}
          aria-label={getLanguageCodeAriaLabel(id, label)}
          role="button"
          lang={id}
        >
          {label}
          {isFetchingTerms && nextLanguageId === id ? (
            <StyledSpinner />
          ) : (
            siteLanguage === id && <CheckIcon width={12} height={12} />
          )}
        </StyledRow>
      ))}
    </>
  );
};
